import { Component, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import { Play, Pause, RotateCcw, Loader2, CheckCircle2, AlertTriangle, Zap } from "@/lib/icons";

/**
 * Reaction Collision Simulator.
 *
 * 2D box of bouncing reactant particles. Each collision between a red and a
 * blue particle is checked against:
 *   1. Energy: combined KE >= activation energy
 *   2. Orientation: dot of relative velocity with reactive axis > threshold
 * Successful collisions convert pair into a green product particle.
 *
 * Controls: temperature (rms speed scale), concentration (n particles),
 * activation energy line (draggable), catalyst toggle (lowers Ea).
 */

interface P {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 0 = A, 1 = B, 2 = product */
  kind: 0 | 1 | 2;
  r: number;
  /** flash counter */
  flash: number;
}

const W = 560;
const H = 360;
const PARTICLE_R = 6;

export default function CollisionSim() {
  return (
    <ErrorBoundary>
      <CollisionSimInner />
    </ErrorBoundary>
  );
}

function CollisionSimInner() {
  const [tempK, setTempK] = useState(400);
  const [count, setCount] = useState(40);
  const [ea, setEa] = useState(35); // kJ/mol-ish, arbitrary units
  const [catalyst, setCatalyst] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [stats, setStats] = useState({ total: 0, success: 0, products: 0, rate: 0 });
  const [ready, setReady] = useState(false);
  const [fps, setFps] = useState(60);
  const [lowPower, setLowPower] = useState(false);
  const [autoLowPower, setAutoLowPower] = useState(true);
  const [runtimeError, setRuntimeError] = useState<{ message: string; stack?: string } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const histRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<P[]>([]);
  const animRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const collisionWindowRef = useRef<number[]>([]); // timestamps of successful collisions
  // Accumulate stats between throttled flushes so we don't trigger a React
  // re-render every animation frame (that caused the page to freeze on
  // slower devices and presented as a white screen).
  const statsAccRef = useRef({ total: 0, success: 0, products: 0 });
  const lastStatsFlushRef = useRef(0);
  // FPS tracking + low-power decisioning
  const fpsSamplesRef = useRef<number[]>([]);
  const lastFpsFlushRef = useRef(0);
  const lowPowerRef = useRef(false);
  const lowSinceRef = useRef(0);
  const highSinceRef = useRef(0);
  // Effective particle count after low-power throttling
  const effectiveCountRef = useRef(count);

  // Capture global errors so a render crash shows in the overlay rather than a
  // blank screen.
  useEffect(() => {
    const onErr = (e: ErrorEvent) => {
      setRuntimeError({ message: e.message, stack: e.error?.stack });
    };
    const onRej = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      setRuntimeError({
        message: typeof r === "string" ? r : r?.message ?? "Unhandled promise rejection",
        stack: r?.stack,
      });
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  const effectiveEa = catalyst ? ea * 0.55 : ea;

  // Initialize / reset particles when count changes
  useEffect(() => {
    const arr: P[] = [];
    const speed = Math.sqrt(tempK / 300) * 80;
    // When in low-power mode, cap particle count to keep things smooth.
    const target = lowPowerRef.current ? Math.min(count, 30) : count;
    effectiveCountRef.current = target;
    for (let i = 0; i < target; i++) {
      const angle = Math.random() * Math.PI * 2;
      arr.push({
        x: Math.random() * (W - 20) + 10,
        y: Math.random() * (H - 20) + 10,
        vx: Math.cos(angle) * speed * (0.6 + Math.random() * 0.8),
        vy: Math.sin(angle) * speed * (0.6 + Math.random() * 0.8),
        kind: i % 2 === 0 ? 0 : 1,
        r: PARTICLE_R,
        flash: 0,
      });
    }
    particlesRef.current = arr;
    setStats({ total: 0, success: 0, products: 0, rate: 0 });
    collisionWindowRef.current = [];
  }, [count, lowPower]);

  // Re-scale velocities when temperature changes (don't re-init positions)
  useEffect(() => {
    const target = Math.sqrt(tempK / 300) * 80;
    const ps = particlesRef.current;
    for (const p of ps) {
      const cur = Math.hypot(p.vx, p.vy) || 1;
      const k = target / cur;
      p.vx *= k;
      p.vy *= k;
    }
  }, [tempK]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d")!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Mark engine ready on first paint so the spinner clears.
    requestAnimationFrame(() => {
      if (!cancelled) setReady(true);
    });

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      // Background grid
      ctx.fillStyle = "hsl(220 13% 10%)";
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "hsl(220 13% 28%)";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

      const ps = particlesRef.current;
      for (const p of ps) {
        if (p.flash > 0) {
          ctx.fillStyle = "hsl(45 100% 65%)";
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r + 4, 0, Math.PI * 2);
          ctx.fill();
          p.flash -= 1;
        }
        ctx.fillStyle =
          p.kind === 0
            ? "hsl(0 75% 60%)"
            : p.kind === 1
              ? "hsl(210 80% 60%)"
              : "hsl(140 65% 50%)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = (now: number) => {
      const dt = lastTimeRef.current ? Math.min(0.05, (now - lastTimeRef.current) / 1000) : 0.016;
      lastTimeRef.current = now;

      // Track instantaneous FPS, decide low-power mode.
      if (dt > 0) {
        fpsSamplesRef.current.push(1 / dt);
        if (fpsSamplesRef.current.length > 60) fpsSamplesRef.current.shift();
      }
      if (now - lastFpsFlushRef.current > 500) {
        lastFpsFlushRef.current = now;
        const samples = fpsSamplesRef.current;
        const avg = samples.length
          ? samples.reduce((a, b) => a + b, 0) / samples.length
          : 60;
        setFps(avg);
        if (autoLowPower) {
          if (avg < 35) {
            if (!lowSinceRef.current) lowSinceRef.current = now;
            highSinceRef.current = 0;
            // Sustained low FPS for >1s → enter low-power
            if (!lowPowerRef.current && now - lowSinceRef.current > 1000) {
              lowPowerRef.current = true;
              setLowPower(true);
            }
          } else if (avg > 55) {
            if (!highSinceRef.current) highSinceRef.current = now;
            lowSinceRef.current = 0;
            if (lowPowerRef.current && now - highSinceRef.current > 3000) {
              lowPowerRef.current = false;
              setLowPower(false);
            }
          } else {
            lowSinceRef.current = 0;
            highSinceRef.current = 0;
          }
        }
      }

      const ps = particlesRef.current;
      // Move
      for (const p of ps) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        if (p.x < p.r) {
          p.x = p.r;
          p.vx *= -1;
        } else if (p.x > W - p.r) {
          p.x = W - p.r;
          p.vx *= -1;
        }
        if (p.y < p.r) {
          p.y = p.r;
          p.vy *= -1;
        } else if (p.y > H - p.r) {
          p.y = H - p.r;
          p.vy *= -1;
        }
      }

      // Collisions (O(n^2) — fine at n<=120)
      let collisionsThisFrame = 0;
      let successesThisFrame = 0;
      let productsThisFrame = 0;
      for (let i = 0; i < ps.length; i++) {
        for (let j = i + 1; j < ps.length; j++) {
          const a = ps[i];
          const b = ps[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d2 = dx * dx + dy * dy;
          const minD = a.r + b.r;
          if (d2 < minD * minD) {
            // Elastic bounce (equal mass)
            const d = Math.sqrt(d2) || 1;
            const nx = dx / d;
            const ny = dy / d;
            const vrelx = a.vx - b.vx;
            const vrely = a.vy - b.vy;
            const vn = vrelx * nx + vrely * ny;
            if (vn > 0) continue; // separating
            a.vx -= vn * nx;
            a.vy -= vn * ny;
            b.vx += vn * nx;
            b.vy += vn * ny;
            // Push apart slightly
            const overlap = minD - d;
            a.x -= (overlap / 2) * nx;
            a.y -= (overlap / 2) * ny;
            b.x += (overlap / 2) * nx;
            b.y += (overlap / 2) * ny;

            // Reactive collision check (A + B only)
            if ((a.kind === 0 && b.kind === 1) || (a.kind === 1 && b.kind === 0)) {
              collisionsThisFrame++;
              // KE in arbitrary units, scaled so typical ~ ea range
              const ke = 0.5 * (a.vx * a.vx + a.vy * a.vy + b.vx * b.vx + b.vy * b.vy);
              const keScaled = ke / 600; // ~ 0..100
              // Orientation: how head-on the collision is
              const orient = Math.abs(vn) / (Math.hypot(vrelx, vrely) || 1);
              const orientOk = orient > 0.6;
              if (keScaled >= effectiveEa && orientOk) {
                successesThisFrame++;
                productsThisFrame++;
                a.kind = 2;
                b.kind = 2;
                a.flash = 8;
                b.flash = 8;
                collisionWindowRef.current.push(now);
              }
            }
          }
        }
      }

      if (collisionsThisFrame || successesThisFrame) {
        statsAccRef.current.total += collisionsThisFrame;
        statsAccRef.current.success += successesThisFrame;
        statsAccRef.current.products += productsThisFrame;
      }

      // Compute rolling rate (successes/sec) over last 2s
      const cutoff = now - 2000;
      collisionWindowRef.current = collisionWindowRef.current.filter((t) => t >= cutoff);
      const rate = collisionWindowRef.current.length / 2;

      // Flush stats to React at most ~5x/sec.
      if (now - lastStatsFlushRef.current > 200) {
        lastStatsFlushRef.current = now;
        const acc = statsAccRef.current;
        setStats((s) => ({
          total: s.total + acc.total,
          success: s.success + acc.success,
          products: s.products + acc.products,
          rate,
        }));
        statsAccRef.current = { total: 0, success: 0, products: 0 };
      }

      draw();
      drawHistogram();
      // In low-power mode, target ~30 fps by skipping every other frame.
      if (lowPowerRef.current) {
        animRef.current = window.setTimeout(
          () => requestAnimationFrame(step),
          33,
        ) as unknown as number;
      } else {
        animRef.current = requestAnimationFrame(step);
      }
    };

    const drawHistogram = () => {
      const c = histRef.current;
      if (!c) return;
      const hw = c.clientWidth;
      const hh = c.clientHeight;
      c.width = hw * dpr;
      c.height = hh * dpr;
      const hctx = c.getContext("2d")!;
      hctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      hctx.clearRect(0, 0, hw, hh);

      const ps = particlesRef.current;
      const bins = 24;
      const counts = new Array(bins).fill(0);
      let maxV = 0;
      const speeds = ps.map((p) => Math.hypot(p.vx, p.vy));
      for (const s of speeds) if (s > maxV) maxV = s;
      maxV = Math.max(maxV, 1);
      for (const s of speeds) {
        const b = Math.min(bins - 1, Math.floor((s / maxV) * bins));
        counts[b] += 1;
      }
      const maxCount = Math.max(1, ...counts);

      // Bars
      const padL = 4;
      const padR = 4;
      const padT = 6;
      const padB = 14;
      const bw = (hw - padL - padR) / bins;

      // Ea threshold mapped to a speed-equivalent: KE = effectiveEa
      // (simplified: keScaled = 0.5*(va^2+vb^2)/600, with v_a=v_b => 1*v^2/600)
      const eaSpeed = Math.sqrt(effectiveEa * 600);
      const eaX = padL + (Math.min(eaSpeed, maxV) / maxV) * (hw - padL - padR);

      for (let i = 0; i < bins; i++) {
        const h = (counts[i] / maxCount) * (hh - padT - padB);
        const x = padL + i * bw;
        const binSpeed = ((i + 0.5) / bins) * maxV;
        hctx.fillStyle =
          binSpeed >= eaSpeed ? "hsl(140 65% 50%)" : "hsl(210 60% 50%)";
        hctx.fillRect(x + 0.5, hh - padB - h, bw - 1, h);
      }

      // Ea line
      hctx.strokeStyle = "hsl(45 95% 60%)";
      hctx.lineWidth = 2;
      hctx.setLineDash([4, 3]);
      hctx.beginPath();
      hctx.moveTo(eaX, padT);
      hctx.lineTo(eaX, hh - padB);
      hctx.stroke();
      hctx.setLineDash([]);
      hctx.fillStyle = "hsl(45 95% 60%)";
      hctx.font = "10px ui-sans-serif, system-ui";
      hctx.fillText("Ea", eaX + 3, padT + 9);

      // Axis label
      hctx.fillStyle = "hsl(220 13% 50%)";
      hctx.fillText("speed →", padL, hh - 2);
    };

    if (playing) {
      animRef.current = requestAnimationFrame(step);
    } else {
      draw();
      drawHistogram();
    }
    return () => {
      cancelled = true;
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
        clearTimeout(animRef.current);
      }
      lastTimeRef.current = 0;
    };
  }, [playing, effectiveEa, autoLowPower]);

  // Sync manual low-power toggle to ref
  useEffect(() => {
    lowPowerRef.current = lowPower;
  }, [lowPower]);

  const successPct = stats.total ? (stats.success / stats.total) * 100 : 0;

  const reset = () => {
    setStats({ total: 0, success: 0, products: 0, rate: 0 });
    collisionWindowRef.current = [];
    setCount((c) => c); // force re-init via the count effect
    // Re-init kinds explicitly
    particlesRef.current.forEach((p, i) => {
      p.kind = i % 2 === 0 ? 0 : 1;
      p.flash = 0;
    });
  };

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Reaction Collision Simulator</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Watch reactant particles bounce. Only collisions with enough energy{" "}
            <em>and</em> the right orientation react. Tweak temperature, concentration, and
            the activation energy line to see how reaction rate responds.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
          {/* Sim canvas + histogram */}
          <div className="space-y-3 min-w-0">
            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <div className="flex items-center justify-between mb-2 text-xs">
                <StatusIndicator ready={ready} fps={fps} lowPower={lowPower} />
                <label className="flex items-center gap-1.5 text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoLowPower}
                    onChange={(e) => setAutoLowPower(e.target.checked)}
                    className="accent-primary"
                  />
                  Auto low-power
                </label>
              </div>
              <div className="relative mx-auto" style={{ width: W, maxWidth: "100%" }}>
                <canvas
                  ref={canvasRef}
                  style={{ width: W, height: H, maxWidth: "100%" }}
                  className="block"
                  aria-label="Particle collision simulation"
                />
                {!ready && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/60 backdrop-blur-sm rounded-md">
                    <Loader2 className="animate-spin text-primary" size={28} />
                    <span className="text-xs text-muted-foreground">Initializing simulation…</span>
                  </div>
                )}
                {runtimeError && (
                  <ErrorOverlay error={runtimeError} onDismiss={() => setRuntimeError(null)} />
                )}
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => setPlaying((p) => !p)}
                  className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <button
                  onClick={reset}
                  className="h-9 w-9 rounded-full bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center"
                  aria-label="Reset"
                >
                  <RotateCcw size={14} />
                </button>
                <div className="flex gap-4 ml-2 text-xs font-mono text-muted-foreground">
                  <span>
                    Successful: <span className="text-foreground">{stats.success}</span> /{" "}
                    {stats.total} ({successPct.toFixed(0)}%)
                  </span>
                  <span>
                    Rate: <span className="text-foreground">{stats.rate.toFixed(1)}/s</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-tile-bg p-3">
              <h2 className="text-sm font-semibold text-foreground mb-2">
                Speed distribution (Maxwell–Boltzmann shape)
              </h2>
              <canvas
                ref={histRef}
                className="w-full h-[120px] block"
                aria-label="Speed histogram"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Green bars: particles at or above the activation energy. Raising
                temperature shifts the distribution right; a catalyst lowers the Ea line.
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="rounded-lg border border-border bg-tile-bg p-3 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Controls</h2>
            <Slider
              label="Temperature"
              value={tempK}
              min={150}
              max={900}
              step={10}
              unit="K"
              onChange={setTempK}
            />
            <Slider
              label="Concentration"
              value={count}
              min={10}
              max={100}
              step={2}
              unit="particles"
              onChange={setCount}
            />
            <Slider
              label="Activation energy"
              value={ea}
              min={5}
              max={80}
              step={1}
              unit="kJ/mol"
              onChange={setEa}
            />
            <label className="flex items-center justify-between text-xs text-foreground">
              <span>Catalyst (lowers Eₐ ~45%)</span>
              <input
                type="checkbox"
                checked={catalyst}
                onChange={(e) => setCatalyst(e.target.checked)}
                className="accent-primary"
              />
            </label>
            <label className="flex items-center justify-between text-xs text-foreground">
              <span className="flex items-center gap-1">
                <Zap size={12} /> Low-power mode
              </span>
              <input
                type="checkbox"
                checked={lowPower}
                onChange={(e) => {
                  setLowPower(e.target.checked);
                  setAutoLowPower(false);
                }}
                className="accent-primary"
              />
            </label>

            <div className="pt-3 border-t border-border space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-[hsl(0_75%_60%)]" />
                <span className="text-muted-foreground">Reactant A</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-[hsl(210_80%_60%)]" />
                <span className="text-muted-foreground">Reactant B</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-[hsl(140_65%_50%)]" />
                <span className="text-muted-foreground">Product AB</span>
              </div>
            </div>
          </div>
        </div>
      </Main>
    </div>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">
          {value} {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
        aria-label={label}
      />
    </div>
  );
}

function StatusIndicator({
  ready,
  fps,
  lowPower,
}: {
  ready: boolean;
  fps: number;
  lowPower: boolean;
}) {
  if (!ready) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Loader2 size={12} className="animate-spin" />
        Initializing…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 size={12} className="text-[hsl(140_65%_50%)]" />
      <span className="text-foreground">Simulation ready</span>
      <span className="text-muted-foreground font-mono">· {fps.toFixed(0)} fps</span>
      {lowPower && (
        <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 font-medium">
          <Zap size={10} /> Low-power
        </span>
      )}
    </span>
  );
}

function ErrorOverlay({
  error,
  onDismiss,
}: {
  error: { message: string; stack?: string };
  onDismiss: () => void;
}) {
  return (
    <div
      role="alert"
      className="absolute inset-0 flex flex-col bg-background/95 rounded-md border border-destructive/50 p-3 text-xs overflow-auto"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-destructive font-semibold">
          <AlertTriangle size={14} />
          Simulation error
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss error"
        >
          ×
        </button>
      </div>
      <div className="text-foreground mb-2 font-mono break-words">{error.message}</div>
      {error.stack && (
        <pre className="text-[10px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words font-mono">
          {error.stack}
        </pre>
      )}
    </div>
  );
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Attach React component stack so the overlay shows where it crashed.
    if (!error.stack) error.stack = info.componentStack;
    // eslint-disable-next-line no-console
    console.error("[CollisionSim]", error, info);
  }
  render() {
    if (this.state.error) {
      const e = this.state.error;
      return (
        <div className="min-h-screen bg-transparent">
          <AppHeader />
          <Main className="max-w-3xl mx-auto px-4 py-8">
            <div className="rounded-lg border border-destructive/50 bg-tile-bg p-4">
              <div className="flex items-center gap-2 text-destructive font-semibold mb-2">
                <AlertTriangle size={16} />
                Reaction Collision Simulator failed to render
              </div>
              <div className="text-sm text-foreground mb-3 font-mono break-words">
                {e.message}
              </div>
              {e.stack && (
                <pre className="text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words font-mono bg-background/50 rounded p-2 max-h-[300px] overflow-auto">
                  {e.stack}
                </pre>
              )}
              <button
                onClick={() => this.setState({ error: null })}
                className="mt-3 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm"
              >
                Try again
              </button>
            </div>
          </Main>
        </div>
      );
    }
    return this.props.children;
  }
}